<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import * as Sentry from '@sentry/vue';
import { useOrderEditStore } from '@/stores/orderEdit';
import { editErrorText, fillText, ruleErrorText, useEditTexts } from '@/composables/useEditTexts';
import { formatCents } from '@/services/orderEditRules';
import { handOffToCheckout } from '@/services/checkoutHandoff';
import { readPaidId } from '@/services/orderEditUrl';
import EditLineCard from './edit-line-card.vue';
import EditLinkRequest from './edit-link-request.vue';
import EditOutcome from './edit-outcome.vue';

// EAUG-77 §9.2, on the dish-choice page of the menu link (Mode.Menu): a
// button that e-mails the personal edit link, and a block that adds drinks
// (drinks_add: increases only, paid on Stripe). Two placements share the
// orderEdit store, which reads the order once per menu link: part="link" at
// the top of the step, part="drinks" below the dish choice. Session-only like
// the rest of Mode.Menu: nothing is persisted.
const {
  part,
  menuToken,
  hasUnsavedDishes = false,
  paidId,
  pollIntervalMs,
  pollTimeoutMs,
} = defineProps<{
  part: 'link' | 'drinks';
  menuToken: string;
  /** step-menu2: dish picks not saved yet (going to Stripe would lose them). */
  hasUnsavedDishes?: boolean;
  /** Back from Stripe; defaults to this page's `paid` URL parameter. */
  paidId?: number | null;
  /** Test seams; the block uses the spec's 4 s and 60 s. */
  pollIntervalMs?: number;
  pollTimeoutMs?: number;
}>();

const store = useOrderEditStore();
const t = useEditTexts();
const showUnsavedDialog = ref(false);
// Component state, not store state: the store's state goes to Sentry.
const checkoutFallbackUrl = ref<string | null>(null);
let cancelHandoff: (() => void) | null = null;
onBeforeUnmount(() => cancelHandoff?.());

onMounted(() => {
  if (!menuToken) return;
  void store.load({ menuToken });
  if (part !== 'drinks') return;
  const paid = paidId !== undefined ? paidId : readPaidId(window.location.search);
  if (paid !== null) store.startPaidPolling(paid, { intervalMs: pollIntervalMs, timeoutMs: pollTimeoutMs });
});

// Only once the read answered for this menu link. A passed deadline, an
// ineligible booking, the feature off or any failure hides both parts; the
// dish choice goes on as before.
const visible = computed(
  () => store.loadState === 'ready' && store.view?.capability === 'drinks_add' && !store.deadlinePassed,
);
const drinks = computed(() => store.view?.sections.drinks ?? []);
const amount = computed(() =>
  store.quote && store.quote.netCents > 0 ? fillText(t.edit_to_pay, { amount: formatCents(store.quote.netCents) }) : null,
);
const ruleError = computed(() =>
  store.quoteError ? ruleErrorText(t, store.quoteError.code, store.labelOf(store.quoteError.product)) : null,
);
const buttonLabel = computed(() => {
  if (store.submitState === 'redirecting') return t.edit_redirecting;
  return store.quoteChanged ? t.edit_confirm_again : t.edit_menu_drinks_add;
});

function addDrinks() {
  if (!store.canSubmit) return;
  if (hasUnsavedDishes) {
    showUnsavedDialog.value = true;
    return;
  }
  void pay();
}

async function pay() {
  showUnsavedDialog.value = false;
  const result = await store.submit();
  if (result?.status !== 'payment_required') return;
  const url = result.checkoutUrl;
  cancelHandoff = handOffToCheckout(url, {
    onFallback: (topNavigation, error) => {
      checkoutFallbackUrl.value = url;
      // Never the session URL, nor the browser's message (it names it).
      Sentry.captureMessage('Checkout hand-off ignored by the embedding page', {
        level: 'warning',
        tags: { checkout_handoff: 'fallback', top_navigation: topNavigation, mode: 'order_edit_menu' },
        extra: {
          error_name: error instanceof Error ? error.name : undefined,
          visibility: document.visibilityState,
        },
      });
    },
  });
}
</script>

<template>
  <div v-if="part === 'link' && visible" class="menu-order-edit" data-testid="menu-order-edit-link">
    <p class="menu-order-edit__question">{{ t.edit_menu_link_question }}</p>
    <edit-link-request />
  </div>
  <div v-if="part === 'drinks'" class="menu-order-edit">
    <edit-outcome />
    <div v-if="visible" data-testid="menu-order-edit-drinks">
      <div class="bpa-front-module-heading">{{ t.edit_menu_drinks_title }}</div>
      <div class="bpa-front-module--service-items-row">
        <edit-line-card v-for="line in drinks" :key="line.product" :line="line" :frozen="store.submitState !== 'idle'" />
      </div>
      <p v-if="store.quoteState === 'pending'" data-testid="menu-order-edit-quote-pending">{{ t.edit_summary_computing }}</p>
      <p v-if="ruleError" class="menu-order-edit__error" role="alert">{{ ruleError }}</p>
      <p v-if="store.actionError" class="menu-order-edit__error" role="alert" data-testid="menu-order-edit-error">
        {{ editErrorText(t, store.actionError) }}
      </p>
      <p v-if="store.quoteChanged" role="status" data-testid="menu-order-edit-quote-changed">{{ t.edit_quote_changed }}</p>
      <p v-if="amount" class="menu-order-edit__amount" data-testid="menu-order-edit-amount">{{ amount }}</p>
      <div class="menu-order-edit__actions">
        <button
          type="button"
          data-testid="menu-order-edit-add"
          :disabled="!store.canSubmit"
          :class="{ 'is-disabled': !store.canSubmit }"
          class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--primary el-button--default"
          @click="addDrinks"
        >
          <span><strong>{{ buttonLabel }}</strong></span>
        </button>
        <a
          v-if="checkoutFallbackUrl"
          :href="checkoutFallbackUrl"
          target="_top"
          data-testid="menu-order-edit-checkout-fallback"
          class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--primary el-button--default"
        >{{ t.edit_checkout_fallback }}</a>
      </div>
      <p class="menu-order-edit__info" data-testid="menu-order-edit-info">{{ t.edit_menu_info }}</p>
      <edit-link-request />
    </div>
    <v-dialog v-model="showUnsavedDialog" width="auto" scroll-strategy="none" class="dialog-viewport-center">
      <v-card max-width="440" prepend-icon="mdi-alert" :text="t.edit_menu_unsaved_dishes" data-testid="menu-order-edit-unsaved-dialog">
        <template v-slot:actions>
          <v-btn data-testid="menu-order-edit-unsaved-cancel" @click="showUnsavedDialog = false">
            {{ t.edit_menu_unsaved_cancel }}
          </v-btn>
          <v-btn color="primary" data-testid="menu-order-edit-unsaved-continue" @click="pay">
            {{ t.edit_menu_unsaved_continue }}
          </v-btn>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.menu-order-edit {
  margin: 12px 0 20px;
}

.menu-order-edit__question {
  font-weight: 600;
  margin-bottom: 8px;
}

.menu-order-edit__amount {
  margin: 12px 0;
  font-weight: 600;
}

.menu-order-edit__error {
  color: #b00020;
}

.menu-order-edit__actions {
  display: flex;
  gap: 12px;
  margin: 8px 0;
}

.menu-order-edit__info {
  margin-top: 16px;
  font-style: italic;
}
</style>
